// orphaned-ticket-unlock-marker-match.ts
//
// Split out of orphaned-ticket-reconciler.ts (review H1,
// code-review-phase-05.md — that file would have crossed the 200-line
// budget once the timestamp/ticket comparison landed on top of the
// existing Bug C reclaim logic). Owns ONE decision: does a mission-wide
// `unlock-killed-worker.json` marker apply to THIS ticket's most recent
// attempt? Pure decision logic, no I/O of its own — the attempt-started
// timestamp is read by the caller via an injectable function so tests can
// control the comparison without touching the filesystem's real mtime.
//
// The marker is mission-wide (keyed by controlRoot, not ticket/attempt) —
// naively treating "marker present" as "this ticket's worker was unlock-
// killed" would mis-attribute the kill to a LATER ticket that crashed on
// its own, after the unlock already happened. Two guards before naming it:
// (a) if the marker names a ticket, it must match THIS ticket; (b) the
// marker's `released_at` must be STRICTLY newer than this ticket's own most
// recent `attempt_started` (mtime of its `attempts/<id>-<n>/` dir — the
// journal itself carries no wall-clock field, same rationale as
// `readAttemptTimestampsIso`). No attempt dir at all for the ticket means
// the comparison is unknown, so this returns `false` (neutral) rather than
// guessing.
export interface UnlockMarkerForComparison {
  released_at: string;
  ticket?: string;
}

export async function unlockMarkerAppliesToTicket(
  marker: UnlockMarkerForComparison | undefined,
  ticketId: string,
  controlRoot: string | undefined,
  readTicketAttemptStartedAt: (
    controlRoot: string,
    ticketId: string,
  ) => Promise<string | undefined>,
): Promise<boolean> {
  if (!marker) return false;
  if (marker.ticket !== undefined && marker.ticket !== ticketId) return false;
  if (controlRoot === undefined) return false;

  const attemptStartedAt = await readTicketAttemptStartedAt(controlRoot, ticketId);
  if (attemptStartedAt === undefined) return false;

  const markerMs = Date.parse(marker.released_at);
  const attemptMs = Date.parse(attemptStartedAt);
  if (!Number.isFinite(markerMs) || !Number.isFinite(attemptMs)) return false;
  return markerMs > attemptMs;
}
